/**
 * @module modules/animator
 */

/**
 * @typedef {Object} AnimatorAnimation
 * @property {String} enter - Класс, добавляющийся непосредственно перед отображением,
 * указывает начальную точку анимации входа
 * @property {String} enterActive - Класс, добавляющийся в следующем фрейме после отображения,
 * указывает анимирующееся свойство
 * @property {String} enterTo - Класс, добавляющийся в следующем фрейме,
 * указывает конечную точку анимации входа
 * @property {String} leave - Класс, добавляющийся непосредственно перед удалением,
 * указывает начальную точку анимации выхода
 * @property {String} leaveActive - Класс, добавляющийся в следующем фрейме после отображения,
 * указывает анимирующееся свойство
 * @property {String} leaveTo - Класс, добавляющийся в следующем фрейме,
 * указывает конечную точку анимации выхода
 */

/**
 * @description Событие, означающее анимацию входа элемента
 * @event animatorEnter
 */
const enterEvent = new CustomEvent('Animator.enter');
/**
 * @description Событие, означающее анимацию выхода элемента
 * @event animatorLeave
 */
const leaveEvent = new CustomEvent('Animator.leave');

/**
 * @classdesc Класс для анимирования элементов
 * @hideconstructor
 */
class Animator {
  /**
   * @static
   * @method
   * @description Добавляет хуки анимации на элементе
   * @param {HTMLElement} el - Элемент, для которого добавляем анимацию
   * @param {String} animationName - Название анимации
   */
  static animate(el, animationName) {
    const animation = {
      enter: `${animationName}-enter`,
      enterActive: `${animationName}-enter-active`,
      enterTo: `${animationName}-enter-to`,
      leave: `${animationName}-leave`,
      leaveActive: `${animationName}-leave-active`,
      leaveTo: `${animationName}-leave-to`,
    };
    el.classList.add(animation.enter);
    el.addEventListener(
      Animator.enterEvent.type,
      Animator.playEnterAnimation(el, animation),
    );
    el.addEventListener(
      Animator.leaveEvent.type,
      Animator.playLeaveAnimation(el, animation),
    );
  }

  /**
   * @static
   * @method
   * @description Возвращает обработчик для анимации входа
   * @param {HTMLElement} el - Элемент, для которого генерируется обработчик
   * @param {module:modules/animator~AnimatorAnimation} animation - Анимация элемента
   * @listens module:modules/animator~event:animatorEnter
   * @returns {Function}
   */
  static playEnterAnimation(el, animation) {
    return () => {
      el.classList.remove(animation.leaveTo);
      el.classList.add(animation.enter);
      setTimeout(() => {
        el.classList.add(animation.enterActive);
        el.classList.add(animation.enterTo);
      }, 0);
      el.addEventListener('transitionend', () => {
        el.classList.remove(animation.enter);
        el.classList.remove(animation.enterActive);
      });
    };
  }

  /**
   * @static
   * @method
   * @description Возвращает обработчик для анимации выхода
   * @param {HTMLElement} el - Элемент, для которого генерируется обработчик
   * @param {module:modules/animator~AnimatorAnimation} animation - Анимация элемента
   * @listens module:modules/animator~event:animatorLeave
   * @returns {Function}
   */
  static playLeaveAnimation(el, animation) {
    return () => {
      el.classList.remove(animation.enterTo);
      el.classList.add(animation.leave);
      setTimeout(() => {
        el.classList.add(animation.leaveActive);
        el.classList.add(animation.leaveTo);
      }, 0);
      el.addEventListener('transitionend', () => {
        el.classList.remove(animation.leave);
        el.classList.remove(animation.leaveActive);
      });
    };
  }

  /**
   * @static
   * @member {CustomEvent}
   * @readonly
   * @description Событие для анимации входа
   */
  static get enterEvent() {
    return enterEvent;
  }

  /**
   * @static
   * @member {CustomEvent}
   * @readonly
   * @description Событие для анимации выхода
   */
  static get leaveEvent() {
    return leaveEvent;
  }

  /**
   * @static
   * @method
   * @param {HTMLElement} el - Элемент, для которого необходимо проиграть анимацию
   * @fires module:modules/animator~event:animatorEnter
   */
  static enter(el) {
    el.dispatchEvent(Animator.enterEvent);
  }

  /**
   *
   * @param {HTMLElement} el - Элемент, для которого необходимо проиграть анимацию
   * @fires module:modules/animator~event:animatorLeave
   */
  static leave(el) {
    el.dispatchEvent(Animator.leaveEvent);
  }
}

export default Animator;
